import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Edit, Trash2, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';

import { BACKEND_URL } from '../config';

const NewsManagementPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    fullContent: '',
    published: true,
    createdAt: ''
  });

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/news/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNews(response.data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch news articles',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingNews(null);
    setFormData({ title: '', description: '', fullContent: '', published: true, createdAt: '' });
    setShowModal(true);
  };

  const handleEdit = (article) => {
    setEditingNews(article);
    const createdDate = article.createdAt ? new Date(article.createdAt).toISOString().split('T')[0] : '';
    setFormData({
      title: article.title,
      description: article.description,
      fullContent: article.fullContent,
      published: article.published,
      createdAt: createdDate
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      if (editingNews) {
        await axios.put(
          `${BACKEND_URL}/api/news/${editingNews.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast({ title: 'Success', description: 'News article updated successfully' });
      } else {
        await axios.post(
          `${BACKEND_URL}/api/news`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast({ title: 'Success', description: 'News article created successfully' });
      }
      
      setShowModal(false);
      fetchNews();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save news article',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this news article?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/news/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Success', description: 'News article deleted successfully' });
      fetchNews();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete news article',
        variant: 'destructive'
      });
    }
  };

  const togglePublished = async (article) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${BACKEND_URL}/api/news/${article.id}`,
        { published: !article.published },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: 'Success', description: 'News article updated successfully' });
      fetchNews();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update news article',
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#0d6571] shadow-lg">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/admin')}
                className="text-white hover:text-white/80"
              >
                <ArrowLeft className="mr-2" />
                Back to Admin
              </Button>
              <h1 className="text-2xl font-bold text-white">News Management</h1>
            </div>
            <Button onClick={handleCreate} className="bg-white text-[#0d6571] hover:bg-gray-100">
              <Plus className="mr-2" />
              Create News
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : news.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl shadow">
            <p className="text-gray-500 mb-4">No news articles yet</p>
            <Button onClick={handleCreate}>
              <Plus className="mr-2" />
              Create First News Article
            </Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {news.map((article) => (
              <div key={article.id} className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-bold text-gray-900">{article.title}</h3>
                      {article.published ? (
                        <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                          Published
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded-full">
                          Draft
                        </span>
                      )}
                    </div>
                    <p className="text-gray-600 mb-2">{article.description}</p>
                    <p className="text-sm text-gray-400">
                      Created: {new Date(article.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => togglePublished(article)}
                      title={article.published ? 'Unpublish' : 'Publish'}
                    >
                      {article.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(article)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(article.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-6">
                {editingNews ? 'Edit News Article' : 'Create News Article'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Title</label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0d6571]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Short Description</label>
                  <textarea
                    required
                    rows="2"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0d6571]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Full Content</label>
                  <textarea
                    required
                    rows="8"
                    value={formData.fullContent}
                    onChange={(e) => setFormData({ ...formData, fullContent: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0d6571]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Publication Date (Optional)
                  </label>
                  <input
                    type="date"
                    value={formData.createdAt}
                    onChange={(e) => setFormData({ ...formData, createdAt: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0d6571]"
                    placeholder="Leave empty for current date"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Leave empty to use today's date. Set a custom date for historical articles.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="published"
                    checked={formData.published}
                    onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                  />
                  <label htmlFor="published" className="text-sm font-medium">Publish immediately</label>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="submit" className="flex-1">
                    {editingNews ? 'Update' : 'Create'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowModal(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsManagementPage;
